import type { DeploymentResponse } from "@nexus/types"
import { createAsyncThunk } from "@reduxjs/toolkit"

import { embedApi, type ResolvedEmbedChatroom, resolveEmbedChatroom } from "@/common/services/chat"

interface InitializeEmbedChatParams {
  deploymentId: string
  sessionId: string
  isPreview: boolean
  /** Pre-fetched deployment from EmbedChatEntry; skips getPublicInfo when provided */
  preDeployment?: DeploymentResponse
}

/**
 * What a resumed embed session carries into the store. Named rather than
 * inferred: the inferred shape spells out every enum inside the deployment, and
 * a declaration emitter cannot name those portably.
 */
export interface InitializeEmbedChatResult {
  chatId: string
  deploymentId: string
  sessionId: string
  isPreview: boolean
  isNewChatroom: boolean
  deployment: DeploymentResponse
  messages: ResolvedEmbedChatroom["messages"]
  hasMoreHistory: boolean
  topic: string | null
}

/** Why the session could not be initialized, as the slice shows it. */
export interface InitializeEmbedChatRejection {
  message: string
}

/**
 * Initialize embed chat session
 * Fetches deployment metadata, then resumes the session's chatroom with its history
 * (`resolveEmbedChatroom`, shared with the closed-conversation reopen).
 *
 * Uses typed embedApi with Zod validation for end-to-end type safety.
 * When preDeployment is provided (e.g. from EmbedChatEntry landing flow),
 * skips the getPublicInfo call to avoid duplicate network requests.
 */
export const initializeEmbedChat = createAsyncThunk<
  InitializeEmbedChatResult,
  InitializeEmbedChatParams,
  { rejectValue: InitializeEmbedChatRejection }
>(
  "embedChat/initialize",
  async (
    { deploymentId, sessionId, isPreview, preDeployment }: InitializeEmbedChatParams,
    { rejectWithValue }
  ) => {
    try {
      // Phase 1: Fetch deployment (or use pre-fetched from EmbedChatEntry)
      const deployment = preDeployment ?? (await embedApi.getPublicInfo({ deploymentId }))

      const agentId = deployment.agent?.id
      if (!agentId) {
        throw new Error("No agent found in deployment")
      }

      // Phase 2: Resume the session's chatroom and read its history (if it has any)
      const chatroom = await resolveEmbedChatroom({ deploymentId, sessionId, agentId })

      return {
        chatId: chatroom.chatId,
        deploymentId,
        sessionId,
        isPreview,
        isNewChatroom: chatroom.isNewChatroom,
        deployment,
        messages: chatroom.messages,
        hasMoreHistory: chatroom.hasMoreHistory,
        topic: chatroom.topic
      }
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to initialize embed chat"
      return rejectWithValue({
        message: errorMessage
      })
    }
  }
)
